import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { css } from '@linaria/core';

import { useFeatureContext } from 'contexts/FeatureContext.jsx';
import useCurrentRouteKey from 'hooks/useCurrentRouteKey.js';
import useOrgId from 'hooks/useOrgId.js';
import { ensureEmbedScript } from 'utils/embedScript.js';
import useDashboardFabQuery from './useDashboardFabQuery.js';
import {
  createWatchTracker,
  readDashboardFabState,
  recordDismissed,
  recordWatched,
  selectDashboardFabVideoIds,
} from './dashboardFabState.js';

// Sized and placed like the live shows FAB on merchant sites (widgets
// FloatingActionButton): 124x226, or 226x124 for a landscape first video, 8px
// corners, 16px from the corner, 8px on narrow screens. The player size holds
// in every state, as the embed lays out the collapsed FAB from it; the position
// only while collapsed, so the expanded player keeps the embed's own layout.
const fabClassName = css`
  --bam-player-width: 124px;
  --bam-player-height: 226px;
  --bam-player-corner-radius: 8px;
  /* The embed's close button, smaller and tucked into the top right corner. */
  --bam-playlist-fab-close-button-offset: 4px;
  --bam-playlist-fab-close-button-padding: 4px;
  --bam-playlist-fab-close-button-size: 16px;

  &[data-orientation='landscape'] {
    --bam-player-width: 226px;
    --bam-player-height: 124px;
  }

  &[data-view-state='fab'] {
    bottom: 16px;
    right: 16px;
    border-radius: 8px;

    @media (max-width: 479px) {
      bottom: 8px;
      right: 8px;
    }
  }
`;

/**
 * A floating shoppable video player with Bambuser videos for the current page.
 *
 * The element is created by hand rather than rendered by React: a fab-mode
 * bam-playlist moves itself to the end of <body>, and its close button removes
 * it from the page, neither of which React can follow.
 */
const DashboardFab = () => {
  const { features } = useFeatureContext();
  const uid = useSelector((state) => state.sessionState.authUser?.uid);
  // A page served by a nested router matches no ROUTES key; it still gets
  // the videos whose rules have no routes.
  const { routeKeys } = useCurrentRouteKey();
  const { pathname: url } = useLocation();
  const orgId = useOrgId();
  const enabled = !!features?.dashboardFabEnabled && !!uid;

  const { data } = useDashboardFabQuery({ orgId, routeKeys, enabled });

  // The playlist is chosen once per page visit, so a video does not vanish
  // while it is being watched.
  const [playlist, setPlaylist] = useState(null);
  useEffect(() => {
    if (!data || playlist?.url === url) return;
    const videoIds = selectDashboardFabVideoIds(data.videos, readDashboardFabState(uid));
    setPlaylist({
      url,
      fabOrgId: data.orgId,
      videoIds,
      // The FAB takes the shape of the video it plays first.
      orientation: data.videos.find(({ id }) => id === videoIds[0])?.orientation ?? 'portrait',
    });
  }, [data, playlist, uid, url]);

  const current = enabled && playlist?.url === url ? playlist : null;
  const videoIds = current?.videoIds.join(',') ?? '';
  const fabOrgId = current?.fabOrgId;
  const orientation = current?.orientation;

  useEffect(() => {
    if (!videoIds || !fabOrgId) return undefined;

    ensureEmbedScript();
    const element = document.createElement('bam-playlist');
    element.setAttribute('org-id', fabOrgId);
    element.setAttribute('video-ids', videoIds);
    element.setAttribute('mode', 'fab');
    // Play the videos one after another without a hover, as a live FAB does.
    element.setAttribute('autoplay', 'cascade');
    // A click opens the first video from its first frame, not the preview.
    element.setAttribute('expand-from-start', 'true');
    element.setAttribute('collect-metrics', 'false');
    element.setAttribute('data-test-id', 'dashboard-fab');
    element.setAttribute('data-orientation', orientation);
    element.classList.add(fabClassName);

    // Each video counts as watched on its own, once it has played for a
    // moment in the opened FAB.
    const trackWatching = createWatchTracker((videoId) => recordWatched(uid, videoId));
    const onTimeUpdate = (event) => trackWatching(event.detail);
    element.addEventListener('timeupdate', onTimeUpdate);

    // The FAB's close button removes the element and emits no event; anything
    // else that takes it out of the page is this component's own cleanup.
    let isCleaningUp = false;
    const observer = new MutationObserver(() => {
      if (isCleaningUp || element.isConnected) return;
      observer.disconnect();
      recordDismissed(uid, videoIds.split(','));
    });
    observer.observe(document.body, { childList: true });

    document.body.appendChild(element);

    return () => {
      isCleaningUp = true;
      observer.disconnect();
      element.removeEventListener('timeupdate', onTimeUpdate);
      element.remove();
    };
  }, [fabOrgId, orientation, uid, videoIds]);

  return null;
};

export default DashboardFab;
